// EvaluateResult — 19BO+ Line 2 B3 · /ingest/evaluate endpoint 返回值契约
//
// 为什么存在 (llm-native.md §12.2 铁律 4):
//   evaluate 输出是只读 verdict，不包 EditableItem。本 schema 把 endpoint 返回值
//   分三层：itemKind（判别被评的是哪类主观题）+ verdict（复用 EssayAIScoreSchema
//   的评分结构）+ telemetry（计费/可观测性用）。分层让前端只读卡片渲染 verdict、
//   后端计费读 telemetry、持久化只取 verdict——三种消费者各取所需，互不耦合。
//
// 为什么 verdict 复用 EssayAIScoreSchema (DRY · lockstep 契约):
//   EssayAIScoreSchema 早于 B3 就存在（Session 19N interface RESERVATION），已是
//   EssayDraft.aiScore 和 FreeResponse.aiScore 的持久化槽位契约。endpoint 返回的
//   verdict 部分形状必须与持久化槽位一致，否则存入时需要转换（转换是 drift 源头）。
//   复用让 endpoint→持久化的路径零转换：前端拿到 EvaluateResult.verdict 直接存进
//   essayDrafts[id].aiScore。本 schema 只是在 verdict 外层加 itemKind + telemetry，
//   不改 verdict 形状。
//
// 为什么 telemetry 是独立对象 (而非平铺):
//   评分 verdict 是用户可见的产品内容（score/rubric/feedback），telemetry 是平台
//   内部的成本/延迟数据（inputTokens/outputTokens/costRmb/durationMs）。两者受众
//   不同、生命周期不同（verdict 持久化、telemetry 只用于计费日志）、可见性不同
//   （§2.2 visibility gate 下 verdict 可见、telemetry 对用户不可见）。分层对象让
//   前端可以整体丢弃 telemetry 只取 verdict。
//
// 为什么 itemKind 是判别字段 (而非可选):
//   evaluate 服务 essay + free-response 三 variant。前端拿到返回值后需要知道这是
//   哪类题的评分，才能正确路由持久化（essay→essayDrafts.aiScore / free-response→
//   subjectiveAttempts 或 freeResponse.aiScore）。判别字段（非可选 enum）让这个
//   路由在编译期固定，而非运行时猜测。
//
// 扩展契约 (加第 5 类被评题型 · 当前 4 类是主观题全集):
//   1. 加 EVALUATE_ITEM_KIND_VALUES
//   2. EvaluateItemKindSchema 自动跟变（z.enum 派生）
//   零 endpoint 改动（handler 按 itemKind switch prompt 模板）。
import { z } from 'zod';
import { EssayAIScoreSchema } from './essay.js';
// ─── itemKind 判别值 ──────────────────────────────────────────────────────────
/**
 * evaluate 服务的 4 类主观题（按 FreeResponseSchema + EssaySchema 的 kind 联合）。
 * 顺序：essay（长文论述）→ short_answer（简答）→ calculation（计算）
 * → translation（翻译）。排除 mcq/tf 等客观题（它们有确定答案，无需 AI 评分）。
 */
export const EVALUATE_ITEM_KIND_VALUES = [
    'essay',
    'short_answer',
    'calculation',
    'translation',
];
export const EvaluateItemKindSchema = z.enum(EVALUATE_ITEM_KIND_VALUES);
// ─── telemetry（计费 + 可观测性 · 对用户不可见） ───────────────────────────
/**
 * 单次 evaluate 调用的成本/延迟快照。形状对齐 repair-handler 的 cost reporting
 * （inputTokens/outputTokens/costRmb/durationMs）—— evaluate 与 repair 同为
 * per-item 单次 LLM 调用，telemetry 契约同源。
 *
 * 非负约束：tokens/costRmb/durationMs 都是计量值，负值无意义（捕获 provider
 * 返回异常或计量 bug）。durationMs 允许 0（极快 mock 响应）。
 */
const EvaluateTelemetrySchema = z.object({
    /** 输入 token 数（prompt + context bundle）。 */
    inputTokens: z.number().int().nonnegative(),
    /** 输出 token 数（verdict JSON）。 */
    outputTokens: z.number().int().nonnegative(),
    /** 本次调用估算人民币成本（对齐 estimateCostRmb · 1 point = ¥0.01）。 */
    costRmb: z.number().nonnegative(),
    /** 端到端延迟（毫秒 · 含 provider 调用 + zod 校验）。 */
    durationMs: z.number().int().nonnegative(),
}).strict();
// ─── EvaluateResult（endpoint 返回值 · 分层 verdict + telemetry） ───────────
/**
 * /ingest/evaluate endpoint 返回值（§12.2 铁律 4 的可执行落地）。
 *
 * 三层：
 *   - itemKind: 判别被评题型（前端路由持久化用）
 *   - verdict: 评分 verdict（复用 EssayAIScoreSchema · 持久化时直接取此对象）
 *   - telemetry: 成本/延迟（计费用 · 对用户不可见 · 前端可丢弃）
 *
 * 当前消费者：B3.4 前端 essay-view / free-response-view（接 disabled stub）。
 */
export const EvaluateResultSchema = z.object({
    itemKind: EvaluateItemKindSchema,
    verdict: EssayAIScoreSchema,
    telemetry: EvaluateTelemetrySchema,
}).strict();
//# sourceMappingURL=evaluate-result.js.map